/**
 * PennylaneLinkModal - Modal pour rattacher une facture aux éléments Pennylane
 * 
 * Permet de:
 * - Voir les fournisseurs, clients et catégories synchronisés depuis Pennylane
 * - Sélectionner les éléments à lier à la transaction
 * - Valider et envoyer vers Pennylane
 */

import React, { useState, useEffect, useCallback } from 'react';
import getApiUrl from '../utils/getApiUrl';
import { 
  X, 
  Search, 
  Building2, 
  Users, 
  FolderOpen, 
  Check, 
  Send, 
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Sparkles,
  ExternalLink
} from 'lucide-react';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_URL = getApiUrl();

export default function PennylaneLinkModal({ 
  transaction, 
  onClose, 
  onSuccess,
  formatAmount 
}) {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('suppliers'); // suppliers, customers, categories
  
  // Suggestions from Pennylane sync
  const [suggestions, setSuggestions] = useState({
    suppliers: [],
    customers: [],
    categories: []
  });
  const [counts, setCounts] = useState({
    suppliers: 0,
    customers: 0,
    categories: 0
  });
  
  // Category-specific search (separate from main search)
  const [categorySearch, setCategorySearch] = useState('');
  
  // Selected items
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [localClientId, setLocalClientId] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    suppliers: true,
    customers: false,
    categories: true
  });
  
  const [error, setError] = useState(null);
  
  // Filter categories locally based on category search
  const filteredCategories = categorySearch
    ? suggestions.categories.filter(c => 
        c.name?.toLowerCase().includes(categorySearch.toLowerCase())
      )
    : suggestions.categories;

  // Load suggestions
  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (transaction?.id) {
        params.append('transaction_id', transaction.id);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await authFetch(
        `${API_URL}/api/rachel-v2/pennylane-suggestions?${params.toString()}`,
        {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSuggestions(data.suggestions || {
          suppliers: [],
          customers: [],
          categories: []
        });
        setCounts(data.counts || {
          suppliers: 0,
          customers: 0,
          categories: 0
        });
      } else {
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur réseau: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [transaction, searchTerm]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        loadSuggestions();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Save link
  const handleSaveLink = async (autoPush = false) => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await authFetch(`${API_URL}/api/rachel-v2/pennylane-link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: transaction.id,
          source_type: 'cb',
          supplier_id: selectedSupplier?.pennylane_id,
          supplier_name: selectedSupplier?.name,
          customer_id: selectedCustomer?.pennylane_id,
          customer_name: selectedCustomer?.name,
          category_id: selectedCategory?.pennylane_id,
          category_name: selectedCategory?.name,
          client_chantier_id: localClientId,
          notes: notes,
          auto_push: autoPush
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (onSuccess) {
          onSuccess(data);
        }
        onClose();
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur réseau: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getRelevanceIndicator = (score) => {
    if (score >= 80) return { color: 'bg-green-500', label: 'Excellent match' };
    if (score >= 50) return { color: 'bg-orange-500', label: 'Bon match' };
    if (score > 0) return { color: 'bg-yellow-500', label: 'Match possible' };
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-700"
        data-testid="pennylane-link-modal"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-emerald-900/50 to-teal-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Rattacher à Pennylane</h2>
                <p className="text-sm text-slate-400">Rachel propose les éléments à valider</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Transaction Info */}
        {transaction && (
          <div className="p-4 bg-slate-800/50 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Transaction à rattacher</p>
                <p className="font-medium text-white">
                  {transaction.supplier || transaction.supplier_detected || 'Fournisseur à identifier'}
                </p>
                <p className="text-sm text-slate-400 truncate max-w-lg">
                  {transaction.label || transaction.full_line}
                </p>
              </div>
              <div className="text-right">
                <p className={`font-bold text-xl ${
                  (transaction.amount || 0) < 0 ? 'text-red-400' : 'text-green-400'
                }`}>
                  {formatAmount ? formatAmount(transaction.amount) : `${transaction.amount} €`}
                </p>
                <p className="text-xs text-slate-500">{transaction.date}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="p-4 border-b border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher fournisseur, client ou catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[45vh]">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-400">Chargement des suggestions Pennylane...</p>
            </div>
          ) : (
            <div className="space-y-4">
              
              {/* Fournisseurs Section */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('suppliers')}
                  className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-750 transition"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span className="font-medium text-white">Fournisseurs</span>
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                      {counts.suppliers}
                    </span>
                    {selectedSupplier && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {selectedSupplier.name}
                      </span>
                    )}
                  </div>
                  {expandedSections.suppliers ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.suppliers && (
                  <div className="max-h-48 overflow-y-auto">
                    {suggestions.suppliers.length === 0 ? (
                      <p className="p-4 text-center text-slate-500 text-sm">
                        Aucun fournisseur synchronisé
                      </p>
                    ) : (
                      <div className="divide-y divide-slate-700/50">
                        {suggestions.suppliers.slice(0, 20).map((supplier, idx) => {
                          const relevance = getRelevanceIndicator(supplier.relevance_score);
                          const isSelected = selectedSupplier?.pennylane_id === supplier.pennylane_id;
                          
                          return (
                            <button
                              key={supplier.pennylane_id || idx}
                              onClick={() => setSelectedSupplier(isSelected ? null : supplier)}
                              className={`w-full p-3 text-left hover:bg-slate-700/50 transition flex items-center justify-between ${
                                isSelected ? 'bg-green-500/10 border-l-2 border-green-500' : ''
                              }`}
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium">{supplier.name}</span>
                                  {relevance && (
                                    <span className={`w-2 h-2 rounded-full ${relevance.color}`} title={relevance.label} />
                                  )}
                                </div>
                                {supplier.invoice_count > 0 && (
                                  <span className="text-xs text-slate-500">
                                    {supplier.invoice_count} facture(s) • {supplier.total_amount?.toFixed(2)} €
                                  </span>
                                )}
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-green-400" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Catégories/Dossiers Section */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('categories')}
                  className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-750 transition"
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-purple-400" />
                    <span className="font-medium text-white">Catégories / Dossiers Clients</span>
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                      {categorySearch ? `${filteredCategories.length}/${counts.categories}` : counts.categories}
                    </span>
                    {selectedCategory && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {selectedCategory.name}
                      </span>
                    )}
                  </div>
                  {expandedSections.categories ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.categories && (
                  <div className="max-h-64 overflow-y-auto">
                    {/* Search box for categories */}
                    <div className="p-2 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
                      <input
                        type="text"
                        placeholder="Rechercher un dossier client..."
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    {filteredCategories.length === 0 ? (
                      <p className="p-4 text-center text-slate-500 text-sm">
                        {categorySearch ? 'Aucun dossier trouvé' : 'Aucune catégorie synchronisée'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {filteredCategories.map((category, idx) => {
                          const isSelected = selectedCategory?.pennylane_id === category.pennylane_id;
                          
                          return (
                            <button
                              key={category.pennylane_id || idx}
                              onClick={() => setSelectedCategory(isSelected ? null : category)}
                              className={`p-2 text-left rounded hover:bg-slate-700/50 transition flex items-center justify-between ${
                                isSelected ? 'bg-purple-500/20 border border-purple-500/50' : ''
                              }`}
                            >
                              <span className="text-sm text-white truncate">{category.name}</span>
                              {isSelected && <Check className="w-3 h-3 text-purple-400 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Clients Section */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('customers')}
                  className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-750 transition"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-400" />
                    <span className="font-medium text-white">Clients Pennylane</span>
                    <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                      {counts.customers}
                    </span>
                    {selectedCustomer && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {selectedCustomer.name}
                      </span>
                    )}
                  </div>
                  {expandedSections.customers ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.customers && (
                  <div className="max-h-48 overflow-y-auto">
                    {suggestions.customers.length === 0 ? (
                      <p className="p-4 text-center text-slate-500 text-sm">
                        Aucun client synchronisé
                      </p>
                    ) : (
                      <div className="divide-y divide-slate-700/50">
                        {suggestions.customers.slice(0, 20).map((customer, idx) => {
                          const isSelected = selectedCustomer?.pennylane_id === customer.pennylane_id;
                          
                          return (
                            <button
                              key={customer.pennylane_id || idx}
                              onClick={() => setSelectedCustomer(isSelected ? null : customer)}
                              className={`w-full p-3 text-left hover:bg-slate-700/50 transition flex items-center justify-between ${
                                isSelected ? 'bg-green-500/10 border-l-2 border-green-500' : ''
                              }`}
                            >
                              <div>
                                <span className="text-white font-medium">{customer.name}</span>
                                {customer.customer_type && (
                                  <span className="text-xs text-slate-500 ml-2">
                                    ({customer.customer_type})
                                  </span>
                                )}
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-green-400" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Notes (optionnel)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajouter des notes sur ce rattachement..."
                  className="w-full p-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-none"
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50 flex items-center justify-between">
          <div className="text-sm text-slate-400">
            {(selectedSupplier || selectedCategory || selectedCustomer) ? (
              <span className="text-emerald-400">
                {[
                  selectedSupplier && 'Fournisseur',
                  selectedCategory && 'Catégorie',
                  selectedCustomer && 'Client'
                ].filter(Boolean).join(' + ')} sélectionné(s)
              </span>
            ) : (
              'Sélectionnez au moins un élément'
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:bg-slate-700 rounded-lg transition"
            >
              Annuler
            </button>
            
            <button
              onClick={() => handleSaveLink(false)}
              disabled={saving || (!selectedSupplier && !selectedCategory && !selectedCustomer)}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Enregistrer
            </button>
            
            <button
              onClick={() => handleSaveLink(true)}
              disabled={saving || (!selectedSupplier && !selectedCategory && !selectedCustomer)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Valider & Envoyer Pennylane
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
